import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  User, 
  Edit3, 
  TrendingUp, 
  Award, 
  Calendar,
  Send,
  Save,
  X,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import BackButton from './BackButton';
import TechnicianProfile from './TechnicianProfile';
import RevenueDisplay from './RevenueDisplay';
import { LoadingPage } from './LoadingStates';
import { ErrorState } from './EmptyStates';
import { 
  getTeamMemberDetails, 
  overrideCertification, 
  updateRevPayRatio,
  submitTierPromotion 
} from '../services/api';
import { TechnicianProfile as TechnicianProfileType } from '../types';

const TeamMemberView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [profile, setProfile] = useState<TechnicianProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Edit states
  const [editingRevPay, setEditingRevPay] = useState(false);
  const [revPayValue, setRevPayValue] = useState('');
  const [savingRevPay, setSavingRevPay] = useState(false);
  
  // Tier promotion state
  const [showTierPromotion, setShowTierPromotion] = useState(false);
  const [promotionData, setPromotionData] = useState({
    newTier: 2 as 1 | 2 | 3,
    justification: ''
  });
  const [submittingPromotion, setSubmittingPromotion] = useState(false);

  // Certification override state
  const [editingCertId, setEditingCertId] = useState<number | null>(null);
  const [overrideData, setOverrideData] = useState({
    obtained_date: '',
    expiration_date: '',
    notes: ''
  });

  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  const fetchProfile = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getTeamMemberDetails(parseInt(userId));
      setProfile(response.data);
      
      // Initialize rev/pay edit value
      if (response.data.tier?.rev_pay_ratio) {
        setRevPayValue(response.data.tier.rev_pay_ratio.toString());
      }
    } catch (err: any) {
      console.error('Error fetching team member details:', err);
      setError(err.response?.data?.message || 'Failed to load team member details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRevPay = async () => {
    if (!userId || !revPayValue) return;
    
    try {
      setSavingRevPay(true);
      await updateRevPayRatio(parseInt(userId), parseFloat(revPayValue));
      await fetchProfile(); // Refresh
      setEditingRevPay(false);
    } catch (err: any) {
      console.error('Error updating rev/pay ratio:', err);
      alert('Failed to update rev/pay ratio');
    } finally {
      setSavingRevPay(false);
    }
  };

  const handleCertificationOverride = async (certId: number) => {
    try {
      const overridePayload = {
        ...(overrideData.obtained_date && { 
          obtained_date: new Date(overrideData.obtained_date) 
        }),
        ...(overrideData.expiration_date && { 
          expiration_date: new Date(overrideData.expiration_date) 
        }),
        ...(overrideData.notes && { notes: overrideData.notes })
      };
      
      await overrideCertification(certId, overridePayload);
      await fetchProfile(); // Refresh
      setEditingCertId(null);
      setOverrideData({ obtained_date: '', expiration_date: '', notes: '' });
    } catch (err: any) {
      console.error('Error overriding certification:', err);
      alert('Failed to override certification');
    }
  };

  const handleTierPromotion = async () => {
    if (!userId) return;
    
    try {
      setSubmittingPromotion(true);
      await submitTierPromotion(parseInt(userId), promotionData);
      setShowTierPromotion(false);
      setPromotionData({ newTier: 2, justification: '' });
      alert('Tier promotion request submitted successfully');
    } catch (err: any) {
      console.error('Error submitting tier promotion:', err);
      alert('Failed to submit tier promotion request');
    } finally {
      setSubmittingPromotion(false);
    }
  };

  if (loading) {
    return <LoadingPage message="Loading team member details..." />;
  }

  if (error || !profile || !userId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton to="/manager/dashboard" className="mb-6" />
        <ErrorState message={error || 'Team member not found'} onRetry={fetchProfile} />
      </div>
    );
  }

  const canPromote = profile.tier && profile.tier.current_tier < 3;
  const tierProgress = (profile.tier_requirements || []).filter(req => 
    req.is_required && profile.certifications.some(cert => 
      cert.certification_id === req.certification_id && 
      cert.file_path && 
      cert.is_valid &&
      (!cert.expiration_date || new Date(cert.expiration_date) > new Date())
    )
  ).length;
  const totalRequired = (profile.tier_requirements || []).filter(req => req.is_required).length;
  const isReadyForPromotion = tierProgress === totalRequired && totalRequired > 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <BackButton to="/manager/dashboard" className="mb-6" />
      
      {/* Manager Controls */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
          <User className="w-5 h-5" />
          <span>Manager Controls</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Rev/Pay Ratio */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Rev/Pay Ratio</span>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            
            {editingRevPay ? (
              <div className="space-y-2">
                <input
                  type="number"
                  step="0.01"
                  value={revPayValue}
                  onChange={(e) => setRevPayValue(e.target.value)}
                  className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                  placeholder="Enter ratio..."
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveRevPay}
                    disabled={savingRevPay || !revPayValue}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white py-1 px-2 rounded text-sm transition-colors"
                  >
                    {savingRevPay ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingRevPay(false);
                      setRevPayValue(profile.tier?.rev_pay_ratio?.toString() || '');
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-white">
                  {profile.tier?.rev_pay_ratio ? Number(profile.tier.rev_pay_ratio).toFixed(2) : 'Not set'}
                </span>
                <button
                  onClick={() => setEditingRevPay(true)}
                  className="text-blue-400 hover:text-blue-300"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Tier Promotion */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Tier Promotion</span>
              <Award className="w-4 h-4 text-purple-400" />
            </div>
            
            <div className="space-y-2">
              <div className="text-lg font-bold text-white">
                Tier {profile.tier?.current_tier || 1}
              </div>
              
              {isReadyForPromotion ? (
                <div className="flex items-center space-x-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Ready for promotion</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{tierProgress}/{totalRequired} requirements met</span>
                </div>
              )}
              
              {canPromote && (
                <button
                  onClick={() => setShowTierPromotion(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-1 px-2 rounded text-sm transition-colors"
                >
                  Submit Promotion
                </button>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Quick Actions</span>
              <Send className="w-4 h-4 text-blue-400" />
            </div>
            
            <div className="space-y-2">
              <button
                onClick={() => {/* TODO: Send reminder email */}}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded text-sm transition-colors"
              >
                Send Reminder
              </button>
              <button
                onClick={() => {/* TODO: Generate report */}}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-1 px-2 rounded text-sm transition-colors"
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Technician Profile */}
      <TechnicianProfile 
        userId={parseInt(userId)} 
        isManager={true}
        canEdit={true}
      />

      {/* Revenue Display */}
      <div className="mt-6">
        <RevenueDisplay userId={parseInt(userId)} showComparison={false} />
      </div>

      {/* Tier Promotion Modal */}
      {showTierPromotion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Submit Tier Promotion
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Promote to Tier
                </label>
                <select
                  value={promotionData.newTier}
                  onChange={(e) => setPromotionData(prev => ({ 
                    ...prev, 
                    newTier: parseInt(e.target.value) as 1 | 2 | 3 
                  }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  {profile.tier && profile.tier.current_tier < 2 && (
                    <option value={2}>Tier 2</option>
                  )}
                  {profile.tier && profile.tier.current_tier < 3 && (
                    <option value={3}>Tier 3</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Justification *
                </label>
                <textarea
                  value={promotionData.justification}
                  onChange={(e) => setPromotionData(prev => ({ 
                    ...prev, 
                    justification: e.target.value 
                  }))}
                  rows={4}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="Provide justification for this tier promotion..."
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleTierPromotion}
                disabled={!promotionData.justification.trim() || submittingPromotion}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white py-2 px-4 rounded transition-colors"
              >
                {submittingPromotion ? 'Submitting...' : 'Submit Promotion'}
              </button>
              <button
                onClick={() => {
                  setShowTierPromotion(false);
                  setPromotionData({ newTier: 2, justification: '' });
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberView;
